import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass } from '../../../components/ui/FormField'
import CampoSenha from '../../../components/CampoSenha'
import {
  useUsuarios, useCreateUsuario, useUpdateUsuario, useDeleteUsuario,
  useRoles, useCreateRole, useUpdateRole, useDeleteRole,
  usePermissoes,
} from '../hooks/useAcesso'
import { usuarioFormSchema, roleFormSchema, type UsuarioFormData, type RoleFormData } from '../schemas'
import type { Usuario, Role } from '../types'

type Tab = 'usuarios' | 'roles' | 'permissoes'

export function AcessoPage() {
  const [tab, setTab] = useState<Tab>('usuarios')

  return (
    <div className="space-y-4">
      <PageHeader title="Controle de Acesso" description="Gerenciar usuarios, roles e permissoes do sistema" />

      {/* Tabs */}
      <div className="border-b border-compumaq-dourado/25">
        <nav className="flex gap-6 -mb-px">
          {(['usuarios', 'roles', 'permissoes'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`pb-3 text-sm font-medium capitalize border-b-2 transition-colors ${
                tab === t
                  ? 'border-compumaq-dourado text-compumaq-doradoClaro'
                  : 'border-transparent text-compumaq-textoSuave hover:text-compumaq-texto'
              }`}
            >
              {t === 'usuarios' ? 'Usuarios' : t === 'roles' ? 'Roles' : 'Permissoes'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'usuarios' && <UsuariosTab />}
      {tab === 'roles' && <RolesTab />}
      {tab === 'permissoes' && <PermissoesTab />}
    </div>
  )
}

// ── Usuarios Tab ─────────────────────────────────────────────

function UsuariosTab() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState({ nome: '', email: '' })
  const [filters, setFilters] = useState({ nome: '', email: '' })
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Usuario | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Usuario | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data: rolesData } = useRoles({ limit: 100 })
  const { data, isLoading } = useUsuarios({ ...search, page, limit: 20 })
  const createUsuario = useCreateUsuario()
  const updateUsuario = useUpdateUsuario()
  const deleteUsuario = useDeleteUsuario()

  const form = useForm<UsuarioFormData>({
    resolver: zodResolver(usuarioFormSchema),
    defaultValues: { nome: '', email: '', senha: '', ativo: true, roles: [] },
  })

  const allRoles = rolesData?.data ?? []

  function openCreate() {
    setEditing(null)
    form.reset({ nome: '', email: '', senha: '', ativo: true, roles: [] })
    setModalOpen(true)
  }

  function openEdit(u: Usuario) {
    setEditing(u)
    form.reset({ nome: u.nome, email: u.email, senha: '', ativo: u.ativo, roles: u.roles })
    setModalOpen(true)
  }

  async function handleSubmit(formData: UsuarioFormData) {
    try {
      const payload: any = {
        nome: formData.nome,
        email: formData.email,
        ativo: formData.ativo ?? true,
        roles: formData.roles ?? [],
      }
      if (formData.senha) payload.senha = formData.senha

      if (editing) {
        await updateUsuario.mutateAsync({ id: editing.idusuario, data: payload })
        setAlert({ type: 'success', message: 'Usuario atualizado com sucesso' })
      } else {
        if (!formData.senha) {
          form.setError('senha', { message: 'Senha obrigatoria para novo usuario' })
          return
        }
        await createUsuario.mutateAsync({ ...payload, senha: formData.senha! })
        setAlert({ type: 'success', message: 'Usuario cadastrado com sucesso' })
      }
      setModalOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar usuario' })
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteUsuario.mutateAsync(deleteTarget.idusuario)
      setAlert({ type: 'success', message: 'Usuario excluido com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir usuario' })
    } finally {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="space-y-4">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Filtros */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Nome</label>
            <input
              className={inputClass}
              placeholder="Filtrar por nome..."
              value={filters.nome}
              onChange={e => setFilters(f => ({ ...f, nome: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && (setPage(1), setSearch({ ...filters }))}
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">E-mail</label>
            <input
              className={inputClass}
              placeholder="Filtrar por e-mail..."
              value={filters.email}
              onChange={e => setFilters(f => ({ ...f, email: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && (setPage(1), setSearch({ ...filters }))}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setPage(1); setSearch({ ...filters }) }} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">Buscar</button>
            <button onClick={() => { setFilters({ nome: '', email: '' }); setSearch({ nome: '', email: '' }); setPage(1) }} className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Limpar</button>
          </div>
          <button onClick={openCreate} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro ml-auto">
            + Novo Usuario
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">👤</p>
            <p className="font-medium">Nenhum usuario encontrado</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">ID</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Nome</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">E-mail</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Roles</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Status</th>
                <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {data.data.map((u) => (
                <tr key={u.idusuario} className="hover:bg-compumaq-preto">
                  <td className="px-4 py-3 text-compumaq-textoSuave">{u.idusuario}</td>
                  <td className="px-4 py-3 font-medium text-compumaq-texto">{u.nome}</td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length ? u.roles.map(r => (
                        <span key={r} className="inline-block bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded">{r}</span>
                      )) : <span className="text-compumaq-textoSuave text-xs">sem role</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${u.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {u.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(u)} className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">Editar</button>
                      <button onClick={() => setDeleteTarget(u)} className="text-xs text-red-600 hover:text-red-800 font-medium">Excluir</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginacao simples */}
      {data?.meta && data.meta.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-40">Anterior</button>
          <span className="px-3 py-1 text-sm text-compumaq-textoSuave">{page} / {data.meta.totalPages}</span>
          <button disabled={page >= data.meta.totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-40">Proximo</button>
        </div>
      )}

      {/* Modal form */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Usuario' : 'Novo Usuario'}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField label="Nome" required error={form.formState.errors.nome?.message}>
            <input className={inputClass} {...form.register('nome')} placeholder="Nome completo" />
          </FormField>
          <FormField label="E-mail" required error={form.formState.errors.email?.message}>
            <input className={inputClass} type="email" {...form.register('email')} placeholder="email@exemplo.com" />
          </FormField>
          <FormField label={editing ? 'Nova Senha (deixe em branco para manter)' : 'Senha'} error={form.formState.errors.senha?.message}>
            <CampoSenha className={inputClass} {...form.register('senha')} placeholder={editing ? 'Nova senha (opcional)' : 'Minimo 6 caracteres'} />
          </FormField>
          <FormField label="Roles">
            <div className="flex flex-wrap gap-2">
              {allRoles.filter(r => r.ativo).map(r => {
                const selected = form.watch('roles')?.includes(r.nome)
                return (
                  <label key={r.idrole} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-colors ${selected ? 'border-compumaq-dourado bg-compumaq-dourado/10 text-compumaq-doradoClaro' : 'border-compumaq-dourado/25 hover:bg-compumaq-preto'}`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-compumaq-dourado/25 text-compumaq-doradoClaro focus:ring-compumaq-dourado"
                      checked={!!selected}
                      onChange={e => {
                        const cur = form.getValues('roles') ?? []
                        const newRoles = e.target.checked ? [...cur, r.nome] : cur.filter(x => x !== r.nome)
                        form.setValue('roles', newRoles, { shouldDirty: true, shouldTouch: true })
                      }}
                    />
                    {r.nome}
                  </label>
                )
              })}
              {allRoles.filter(r => r.ativo).length === 0 && <p className="text-sm text-compumaq-textoSuave">Nenhuma role disponivel</p>}
            </div>
          </FormField>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="ativo-u" {...form.register('ativo')} className="w-4 h-4 rounded" />
            <label htmlFor="ativo-u" className="text-sm text-compumaq-texto">Usuario ativo</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Cancelar</button>
            <button type="submit" disabled={createUsuario.isPending || updateUsuario.isPending} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {createUsuario.isPending || updateUsuario.isPending ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Usuario"
        message={`Tem certeza que deseja excluir o usuario "${deleteTarget?.nome}"?`}
        loading={deleteUsuario.isPending}
      />
    </div>
  )
}

// ── Roles Tab ────────────────────────────────────────────────

function RolesTab() {
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Role | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data: permissoesData } = usePermissoes()
  const { data, isLoading } = useRoles({ page, limit: 20 })
  const createRole = useCreateRole()
  const updateRole = useUpdateRole()
  const deleteRole = useDeleteRole()

  const allPermissoes = permissoesData?.data ?? []

  const form = useForm<RoleFormData>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: { nome: '', descricao: '', ativo: true, permissoes: [] },
  })

  function openCreate() {
    setEditing(null)
    form.reset({ nome: '', descricao: '', ativo: true, permissoes: [] })
    setModalOpen(true)
  }

  function openEdit(r: Role) {
    setEditing(r)
    form.reset({
      nome: r.nome,
      descricao: r.descricao ?? '',
      ativo: r.ativo,
      permissoes: r.permissoes.map(p => p.idpermissao),
    })
    setModalOpen(true)
  }

  async function handleSubmit(formData: RoleFormData) {
    try {
      const payload = {
        nome: formData.nome,
        descricao: formData.descricao || undefined,
        ativo: formData.ativo ?? true,
        permissoes: formData.permissoes ?? [],
      }
      if (editing) {
        await updateRole.mutateAsync({ id: editing.idrole, data: payload })
        setAlert({ type: 'success', message: 'Role atualizada com sucesso' })
      } else {
        await createRole.mutateAsync(payload)
        setAlert({ type: 'success', message: 'Role cadastrada com sucesso' })
      }
      setModalOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar role' })
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteRole.mutateAsync(deleteTarget.idrole)
      setAlert({ type: 'success', message: 'Role excluida com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir role' })
    } finally {
      setDeleteTarget(null)
    }
  }

  // Agrupar permissoes por recurso para exibicao no modal
  const permissoesPorRecurso = allPermissoes.reduce<Record<string, typeof allPermissoes>>((acc, p) => {
    ;(acc[p.recurso] = acc[p.recurso] ?? []).push(p)
    return acc
  }, {})

  return (
    <div className="space-y-4">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="flex justify-end">
        <button onClick={openCreate} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
          + Nova Role
        </button>
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">🔑</p>
            <p className="font-medium">Nenhuma role encontrada</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">ID</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Nome</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Descricao</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Permissoes</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Status</th>
                <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {data.data.map((r) => (
                <tr key={r.idrole} className="hover:bg-compumaq-preto">
                  <td className="px-4 py-3 text-compumaq-textoSuave">{r.idrole}</td>
                  <td className="px-4 py-3 font-medium text-compumaq-texto">{r.nome}</td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">{r.descricao ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-compumaq-textoSuave">{r.permissoes.length} permissao(oes)</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${r.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {r.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(r)} className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">Editar</button>
                      <button onClick={() => setDeleteTarget(r)} className="text-xs text-red-600 hover:text-red-800 font-medium">Excluir</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data?.meta && data.meta.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-40">Anterior</button>
          <span className="px-3 py-1 text-sm text-compumaq-textoSuave">{page} / {data.meta.totalPages}</span>
          <button disabled={page >= data.meta.totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-40">Proximo</button>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Role' : 'Nova Role'}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField label="Nome" required error={form.formState.errors.nome?.message}>
            <input className={inputClass} {...form.register('nome')} placeholder="Nome da role (ex: admin)" />
          </FormField>
          <FormField label="Descricao" error={form.formState.errors.descricao?.message}>
            <input className={inputClass} {...form.register('descricao')} placeholder="Descricao opcional" />
          </FormField>
          <div>
            <p className="text-sm font-medium text-compumaq-texto mb-2">Permissoes</p>
            <div className="max-h-64 overflow-y-auto border border-compumaq-dourado/25 rounded-lg p-3 space-y-3">
              {Object.entries(permissoesPorRecurso).map(([recurso, perms]) => (
                <div key={recurso}>
                  <p className="text-xs font-semibold text-compumaq-textoSuave uppercase mb-1.5">{recurso}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {perms.map(p => {
                      const selected = form.watch('permissoes')?.includes(p.idpermissao)
                      return (
                        <label key={p.idpermissao} className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer text-xs transition-colors ${selected ? 'border-compumaq-dourado bg-compumaq-dourado/10 text-compumaq-doradoClaro' : 'border-compumaq-dourado/25 hover:bg-compumaq-preto'}`}>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={!!selected}
                            onChange={e => {
                              const cur = form.getValues('permissoes') ?? []
                              form.setValue('permissoes', e.target.checked ? [...cur, p.idpermissao] : cur.filter(x => x !== p.idpermissao))
                            }}
                          />
                          {p.acao}
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
              {Object.keys(permissoesPorRecurso).length === 0 && (
                <p className="text-sm text-compumaq-textoSuave text-center py-4">Nenhuma permissao cadastrada</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="ativo-r" {...form.register('ativo')} className="w-4 h-4 rounded" />
            <label htmlFor="ativo-r" className="text-sm text-compumaq-texto">Role ativa</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Cancelar</button>
            <button type="submit" disabled={createRole.isPending || updateRole.isPending} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {createRole.isPending || updateRole.isPending ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Role"
        message={`Tem certeza que deseja excluir a role "${deleteTarget?.nome}"?`}
        loading={deleteRole.isPending}
      />
    </div>
  )
}

// ── Permissoes Tab ───────────────────────────────────────────

function PermissoesTab() {
  const { data, isLoading } = usePermissoes()

  const porRecurso = (data?.data ?? []).reduce<Record<string, Array<{ idpermissao: number; recurso: string; acao: string; descricao: string | null; ativo?: boolean }>>>((acc, p) => {
    ;(acc[p.recurso] = acc[p.recurso] ?? []).push(p)
    return acc
  }, {})

  return (
    <div className="space-y-4">
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm p-4">
        <p className="text-sm text-compumaq-textoSuave mb-4">
          Permissoes cadastradas no sistema (somente leitura). Total: {data?.meta?.total ?? 0}
        </p>
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner size="lg" /></div>
        ) : Object.keys(porRecurso).length === 0 ? (
          <div className="text-center py-12 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">🔒</p>
            <p className="font-medium">Nenhuma permissao encontrada</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(porRecurso).map(([recurso, perms]) => (
              <div key={recurso}>
                <h3 className="text-sm font-semibold text-compumaq-texto mb-2 uppercase tracking-wide">{recurso}</h3>
                <div className="flex flex-wrap gap-2">
                  {perms!.map(p => (
                    <span key={p.idpermissao} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${p.ativo !== false ? 'border-compumaq-dourado/25 bg-compumaq-preto text-compumaq-texto' : 'border-red-200 bg-red-50 text-red-700'}`}>
                      <span className="font-bold">{p.acao}</span>
                      {p.descricao && <span className="text-compumaq-textoSuave">• {p.descricao}</span>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
